import {
  EmployeeDto,
  OnboardingPeriodDto,
  QualifyingLifeEventType,
} from '@zorro/clients';
import {
  DateUtilInstance,
  getFirstDayOfMonthAfterDate,
  getFirstDayOfMonthEqualToOrAfterDate,
  getNow,
  parseDateUnknown,
} from '@zorro/shared/formatters';

import {
  maximalDate,
  maybeMaximalDateISO,
  maybeMinimalDateISO,
  minimalDate,
} from './compareDateStrings';

const oldestQleDate = (): DateUtilInstance =>
  getNow().addBusinessDays(3).subtract(60, 'day');
const newestQleDate = (): DateUtilInstance => getNow().add(60, 'day');

type Props = {
  isOpsTool?: boolean;
  eligibleFrom?: EmployeeDto['eligibleFrom'];
  eligibleUntil?: EmployeeDto['eligibleUntil'];
  enrollmentEffectiveUntil?: OnboardingPeriodDto['enrollmentEffectiveUntil'];
  enrollmentEffectiveFrom?: OnboardingPeriodDto['enrollmentEffectiveFrom'];
};

export const getQualifyingLifeEventsDateRange = ({
  isOpsTool = false,
  eligibleFrom,
  eligibleUntil,
  enrollmentEffectiveUntil,
  enrollmentEffectiveFrom,
}: Props = {}): { minEventDate: string; maxEventDate: string } => {
  const minDate = maybeMaximalDateISO(
    enrollmentEffectiveFrom,
    eligibleFrom,
    isOpsTool ? undefined : oldestQleDate()
  );
  const maxDate = maybeMinimalDateISO(
    enrollmentEffectiveUntil,
    eligibleUntil,
    isOpsTool ? undefined : newestQleDate()
  );
  return {
    minEventDate: minDate || '',
    maxEventDate: maxDate || '',
  };
};

export function calculateElectionDates(
  qleType: QualifyingLifeEventType,
  qleEventDate: Date | string,
  planYearEndDate: string,
  eligibleUntil: string | null | undefined
): {
  electionStartDate: DateUtilInstance;
  electionEndDate: DateUtilInstance | undefined;
  expectedStartOfCoverage: DateUtilInstance | undefined;
  maxCoverageEnd: DateUtilInstance;
} {
  const electionStartDate = getNow();
  let electionEndDate: DateUtilInstance | undefined;
  let expectedStartOfCoverage: DateUtilInstance | undefined;
  const maxCoverageEnd = minimalDate(planYearEndDate, eligibleUntil);

  const parsedQleEventDate = parseDateUnknown(qleEventDate);
  const reportEventDeadline = parsedQleEventDate
    .add(60, 'day')
    .subtractBusinessDays(3);

  if (qleType === QualifyingLifeEventType.NEW_DEPENDENT) {
    expectedStartOfCoverage = parsedQleEventDate;
    electionEndDate = reportEventDeadline;
  } else {
    expectedStartOfCoverage = maximalDate(
      getFirstDayOfMonthEqualToOrAfterDate(getNow().addBusinessDays(3)),
      getFirstDayOfMonthAfterDate(qleEventDate)
    );
    if (expectedStartOfCoverage.isAfter(maxCoverageEnd, 'day')) {
      expectedStartOfCoverage = undefined;
    }
    const coverageEndDeadline = maxCoverageEnd
      .subtract(1, 'month')
      .subtractBusinessDays(3);
    electionEndDate = minimalDate(reportEventDeadline, coverageEndDeadline);
    if (electionEndDate.isAfter(maxCoverageEnd, 'day')) {
      electionEndDate = undefined;
    }
  }
  if (electionStartDate.isAfter(electionEndDate, 'day')) {
    electionEndDate = undefined;
  }

  return {
    electionStartDate,
    electionEndDate,
    expectedStartOfCoverage,
    maxCoverageEnd,
  };
}
